import { useEffect, useMemo, useState } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import type { Event } from '@flock-types/events.types';
import { eventsService } from '@/services/events.service';
import {
  WEEKDAYS,
  defaultReminderTiming,
  timingFromEvent,
  isPresetAvailable,
  localToUTC,
  reminderAlreadySent,
  reminderHelperCopy,
  reminderTimingPayload,
  resolveReminderTiming,
  timingsEqual,
  type ReminderPreset,
  type ReminderTiming,
  type WeekdayName,
} from '@/lib/reminderTiming';
import {
  hydrateEventLocation,
  toCreateLocationPayload,
  toUpdateLocationPayload,
  type PickedEventPlace,
} from '@/lib/eventLocation';
import LocationInput from './LocationInput';

/** Convert an ISO string to a datetime-local input value in local time. */
function toLocalInput(iso?: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

interface Props {
  groupId: string;
  /** When set, the modal edits this event instead of creating one. */
  event?: Event | null;
  /** When creating: offer a group picker (cross-group calendar). */
  groups?: Array<{ id: string; name: string }>;
  /** When creating: prefill the start date (time defaults to 7 PM). */
  defaultDate?: Date;
  /** When editing a cross-org event: pin the update to its org. */
  organizationId?: string;
  onSaved: () => void;
  onClose: () => void;
}

/** Prefill value for a day-cell click: that date at 7:00 PM local. */
function toDefaultStart(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T19:00`;
}

export default function EventFormModal({
  groupId,
  event,
  groups,
  defaultDate,
  organizationId,
  onSaved,
  onClose,
}: Props) {
  const [title, setTitle] = useState(event?.title ?? '');
  const [description, setDescription] = useState(event?.description ?? '');
  const [selectedGroupId, setSelectedGroupId] = useState(groupId);
  const [startTime, setStartTime] = useState(() =>
    event?.start_time
      ? toLocalInput(event.start_time)
      : defaultDate
        ? toDefaultStart(defaultDate)
        : ''
  );
  const [endTime, setEndTime] = useState(toLocalInput(event?.end_time));
  const initialLocation = event ? hydrateEventLocation(event) : { address: '', note: '', place: null };
  const [locationAddress, setLocationAddress] = useState(initialLocation.address);
  const [locationNote, setLocationNote] = useState(initialLocation.note);
  const [locationPlace, setLocationPlace] = useState<PickedEventPlace | null>(initialLocation.place);
  const [recurrence, setRecurrence] = useState<'none' | 'weekly' | 'biweekly' | 'monthly'>(
    event?.recurrence ?? 'none'
  );
  const [rsvpDeadline, setRsvpDeadline] = useState(toLocalInput(event?.rsvp_deadline));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const [reminderTiming, setReminderTiming] = useState<ReminderTiming>(() => {
    if (event) return timingFromEvent(event);
    const seed = defaultDate
      ? localToUTC(toDefaultStart(defaultDate), timezone)
      : new Date(Date.now() + 26 * 60 * 60 * 1000);
    return defaultReminderTiming(seed, timezone);
  });

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const startUtc = useMemo(() => {
    if (!startTime) return null;
    return localToUTC(startTime.length === 16 ? `${startTime}:00` : startTime, timezone);
  }, [startTime, timezone]);

  const reminderResolved = startUtc
    ? resolveReminderTiming({
        ...reminderTiming,
        startTime: startUtc,
        timezone,
        allowPast: true,
      })
    : { ok: false as const, error: 'Choose a start time first.' };

  const handleSave = async () => {
    if (!title.trim() || !startTime || busy) return;
    setBusy(true);
    setError(null);
    try {
      const startUtc = localToUTC(startTime.length === 16 ? `${startTime}:00` : startTime, timezone);
      const startUnchanged = event
        ? new Date(event.start_time).getTime() === startUtc.getTime()
        : false;
      const timingUnchanged = event
        ? timingsEqual(reminderTiming, timingFromEvent(event))
        : false;
      if (!event || !startUnchanged || !timingUnchanged) {
        const reminderCheck = resolveReminderTiming({
          ...reminderTiming,
          startTime: startUtc,
          timezone,
          allowPast: true,
        });
        if (!reminderCheck.ok) {
          setError(reminderCheck.error);
          setBusy(false);
          return;
        }
      }
      const timingPayload = reminderTimingPayload(reminderTiming);
      // The backend converts start/end itself via localToUTC(naiveLocal, timezone),
      // so send the raw datetime-local values — an ISO string with Z gets
      // double-converted and shifts the event by the UTC offset.
      if (event) {
        await eventsService.updateEvent(
          {
          id: event.id,
          title: title.trim(),
          description: description.trim() || undefined,
          start_time: startTime,
          end_time: endTime || undefined,
          ...toUpdateLocationPayload(locationAddress, locationNote, locationPlace),
          recurrence,
            rsvp_deadline: rsvpDeadline ? new Date(rsvpDeadline).toISOString() : undefined,
            timezone,
            reminderTiming: timingPayload,
          },
          organizationId
        );
      } else {
        await eventsService.createEvent({
          groupId: selectedGroupId,
          title: title.trim(),
          description: description.trim() || undefined,
          startTime,
          endTime: endTime || undefined,
          ...toCreateLocationPayload(locationAddress, locationNote, locationPlace),
          recurrenceRule: recurrence !== 'none' ? recurrence : undefined,
          rsvpDeadline: rsvpDeadline ? new Date(rsvpDeadline).toISOString() : undefined,
          enableRsvp: true,
          timezone,
          reminderTiming: timingPayload,
        });
      }
      onSaved();
      onClose();
    } catch (e: any) {
      setError(
        e?.response?.data?.message ||
          e?.response?.data?.error ||
          'Could not save the event. Please try again.'
      );
    } finally {
      setBusy(false);
    }
  };

  const inputClass =
    'w-full rounded-lg border border-flock-gray-200 bg-flock-navy-50 px-3 py-2 text-sm outline-none focus:border-flock-navy-400';

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl bg-white p-5 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-base font-semibold text-flock-navy-900">
            {event ? 'Edit Event' : 'New Event'}
          </h2>
          <button onClick={onClose} className="p-1 text-flock-navy-400 hover:text-flock-navy-600">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {error && (
          <div className="mb-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {error}
          </div>
        )}

        <div className="space-y-3">
          {!event && groups && groups.length > 1 && (
            <div>
              <label className="mb-1 block text-xs font-medium text-flock-navy-600">Group *</label>
              <select
                value={selectedGroupId}
                onChange={(e) => setSelectedGroupId(e.target.value)}
                className={inputClass}
              >
                {groups.map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.name}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div>
            <label className="mb-1 block text-xs font-medium text-flock-navy-600">Title *</label>
            <input
              autoFocus
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Wednesday Bible Study"
              className={inputClass}
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-flock-navy-600">Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              className={inputClass}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-xs font-medium text-flock-navy-600">Starts *</label>
              <input
                type="datetime-local"
                value={startTime}
                onChange={(e) => {
                  const next = e.target.value;
                  // Moving the start drags the end along, preserving duration
                  if (next && startTime && endTime) {
                    const delta = new Date(endTime).getTime() - new Date(startTime).getTime();
                    if (delta > 0) {
                      setEndTime(toLocalInput(new Date(new Date(next).getTime() + delta).toISOString()));
                    }
                  }
                  setStartTime(next);
                }}
                className={inputClass}
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-flock-navy-600">Ends</label>
              <input
                type="datetime-local"
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
                className={inputClass}
              />
            </div>
          </div>
          <LocationInput
            address={locationAddress}
            note={locationNote}
            onAddressChange={setLocationAddress}
            onNoteChange={setLocationNote}
            onPlaceChange={setLocationPlace}
            inputClassName={inputClass}
          />
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-xs font-medium text-flock-navy-600">Repeats</label>
              <select
                value={recurrence}
                onChange={(e) => setRecurrence(e.target.value as typeof recurrence)}
                className={inputClass}
              >
                <option value="none">Does not repeat</option>
                <option value="weekly">Weekly</option>
                <option value="biweekly">Every two weeks</option>
                <option value="monthly">Monthly</option>
              </select>
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-flock-navy-600">
                RSVP deadline
              </label>
              <input
                type="datetime-local"
                value={rsvpDeadline}
                onChange={(e) => setRsvpDeadline(e.target.value)}
                className={inputClass}
              />
            </div>
          </div>
          <div>
            <p className="mb-2 text-xs font-medium text-flock-navy-600">Remind the group</p>
            <div className="space-y-2">
              {([
                { value: '1_day', label: '1 day before' },
                { value: '2_days', label: '2 days before' },
                { value: 'weekday_time', label: 'Custom day & time' },
              ] as { value: ReminderPreset; label: string }[]).map((preset) => {
                const extras = {
                  weekday: reminderTiming.weekday ?? 'monday',
                  localTime: reminderTiming.localTime ?? '08:00',
                };
                const available = startUtc
                  ? isPresetAvailable(preset.value, startUtc, timezone, extras)
                  : false;
                const disabled = !available && preset.value !== 'weekday_time';
                return (
                  <label
                    key={preset.value}
                    className={`flex min-h-touch items-center gap-2 text-sm text-flock-navy-800 ${
                      disabled ? 'opacity-40' : ''
                    }`}
                  >
                    <input
                      type="radio"
                      name="reminder-timing"
                      checked={reminderTiming.preset === preset.value}
                      disabled={disabled}
                      onChange={() => setReminderTiming((current) => ({ ...current, preset: preset.value }))}
                    />
                    {preset.label}
                  </label>
                );
              })}
            </div>
            {reminderTiming.preset === 'weekday_time' && (
              <div className="mt-3 grid grid-cols-2 gap-3">
                <div>
                  <label className="mb-1 block text-xs font-medium text-flock-navy-600">Day</label>
                  <select
                    value={reminderTiming.weekday ?? 'monday'}
                    onChange={(e) =>
                      setReminderTiming((current) => ({
                        ...current,
                        preset: 'weekday_time',
                        weekday: e.target.value as WeekdayName,
                      }))
                    }
                    className={inputClass}
                  >
                    {WEEKDAYS.map((day) => (
                      <option key={day.value} value={day.value}>
                        {day.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium text-flock-navy-600">Time</label>
                  <input
                    type="time"
                    value={reminderTiming.localTime ?? '08:00'}
                    onChange={(e) =>
                      setReminderTiming((current) => ({
                        ...current,
                        preset: 'weekday_time',
                        localTime: e.target.value,
                      }))
                    }
                    className={inputClass}
                  />
                </div>
              </div>
            )}
            {(() => {
              const helper = reminderHelperCopy(
                reminderResolved,
                timezone,
                reminderAlreadySent(event)
              );
              return (
                <p className={`mt-2 text-xs ${helper.error ? 'text-red-600' : 'text-flock-navy-500'}`}>
                  {helper.text}
                </p>
              );
            })()}
          </div>
        </div>

        <button
          onClick={handleSave}
          disabled={busy || !title.trim() || !startTime}
          className="min-h-touch mt-4 w-full rounded-lg bg-flock-navy-600 px-4 py-2.5 text-sm font-medium text-white transition hover:bg-flock-navy-700 disabled:opacity-50"
        >
          {busy ? 'Saving…' : event ? 'Save Changes' : 'Create Event'}
        </button>
      </div>
    </div>
  );
}
